import { CalendarDaysIcon, CalendarIcon, DollarSignIcon, ExternalLinkIcon, LaptopIcon, PiggyBankIcon, ReceiptIcon, Stethoscope } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import type { ReactNode } from 'react'

import { ButtonGroup, Modal, TextLink } from 'slab'

import { FirstBillModalCards } from './FirstBillModalCards'

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

const TOTAL_STEPS = 4

type Step = 1 | 2 | 3 | 4

type StepContent = {
  title: string
  layout: 'horizontal' | 'vertical'
  cards: Array<{
    icon: LucideIcon
    title: string
    description: ReactNode
    descriptionSubdued?: boolean
  }>
  footerText?: ReactNode
}

const STEP_CONTENT: Record<Step, StepContent> = {
  1: {
    title: 'What will I be billed for?',
    layout: 'horizontal',
    cards: [
      {
        icon: LaptopIcon,
        title: 'Platform fees',
        description: 'Helps keep Thatch running and includes the regular monthly per employee fee for usage of our platform and any compliance fees.'
      },
      {
        icon: Stethoscope,
        title: 'Healthcare costs',
        description: (
          <>
            Covers everything related to insurance coverage for your employees like insurance premiums and{' '}
            <TextLink
              href='https://support.thatch.com/qualified-medical-expense'
              target='_blank'
              rel='noopener noreferrer'
              color='purple'
            >
              qualified medical expenses
            </TextLink>
            .
          </>
        )
      }
    ]
  },
  2: {
    title: 'When will billing occur?',
    layout: 'horizontal',
    cards: [
      {
        icon: CalendarDaysIcon,
        title: 'On the 1st of each month',
        description: "We'll debit platform fees, which is $45 per eligible employee, per month."
      },
      {
        icon: CalendarIcon,
        title: 'On the 15th of each month',
        description: "We'll debit healthcare costs, which includes premiums and qualified medical expenses."
      }
    ],
    footerText: (
      <>
        We bill for premiums a month in advance because insurance companies require payment before each month's coverage starts. Debiting on the 15th ensures there's enough time for funds to process, allowing insurance coverage to continue without interruption.{' '}
        <TextLink
          href='https://support.thatch.com/invoices-and-billing'
          target='_blank'
          rel='noopener noreferrer'
          color='purple'
        >
          Learn more
          <ExternalLinkIcon className='inline w-4 h-4 ml-1' />
        </TextLink>
        .
      </>
    )
  },
  3: {
    title: 'How will I be billed?',
    layout: 'vertical',
    cards: [
      {
        icon: DollarSignIcon,
        title: "You'll be billed for full healthcare costs",
        description: "Employees' full healthcare costs will be billed each month, even if their costs exceed their allowance - but don't worry! You'll recoup these overages from your employees.",
        descriptionSubdued: true
      },
      {
        icon: PiggyBankIcon,
        title: "You'll recoup healthcare cost overages via payroll deductions",
        description: (
          <>
            If an employee's healthcare costs exceeds their allowance, you'll need to recoup the overage via payroll deductions.{' '}
            <TextLink
              href='https://support.thatch.com/payroll-deductions'
              target='_blank'
              rel='noopener noreferrer'
              color='purple'
            >
              Learn more about how to set up payroll deductions
            </TextLink>
            .
          </>
        )
      }
    ]
  },
  4: {
    title: 'A few last things you should know',
    layout: 'vertical',
    cards: [
      {
        icon: Stethoscope,
        title: 'Employees enrolling in benefits after the 13th',
        description: "You may see what appears to be 'double billing' for your first month. If an employee enrolls in an insurance plan after the 13th of the month, we'll pay for their first month's premium and then bill this to you in next month's bill."
      },
      {
        icon: ReceiptIcon,
        title: 'Qualified medical expense billing',
        description: 'Qualified medical expenses are only billed once spent by your employees - so your monthly bill may be low early in the year but higher near the end of the year when people generally spend more "use it or lose it" dollars.'
      }
    ]
  }
}

export function FirstBillModal ({ open, onOpenChange }: Props) {
  const [step, setStep] = useState<Step>(1)

  useEffect(() => {
    if (open) setStep(1)
  }, [open])

  const stepContent = STEP_CONTENT[step]

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      width='medium'
      aria-describedby={undefined}
    >
      <Modal.Header>
        <Modal.Title>How billing works at Thatch</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {stepContent && (
          <FirstBillModalCards
            title={stepContent.title}
            cards={stepContent.cards}
            layout={stepContent.layout}
            footerText={stepContent.footerText}
          />
        )}
      </Modal.Body>
      <Modal.Footer>
        <ButtonGroup align='right'>
          {step < TOTAL_STEPS && (
            <Modal.Button variant='tertiary-deemphasized'>
              Skip and go to billing
            </Modal.Button>
          )}
          {step > 1 && (
            <Modal.Button
              variant='secondary'
              onClick={() => {
                setStep((step - 1) as Step)
                return false
              }}
            >
              Back
            </Modal.Button>
          )}
          <Modal.Button
            variant='primary'
            onClick={() => {
              if (step < TOTAL_STEPS) {
                setStep((step + 1) as Step)
                return false
              }
            }}
          >
            {step === TOTAL_STEPS ? `Sounds good! (${step}/${TOTAL_STEPS})` : `Next (${step}/${TOTAL_STEPS})`}
          </Modal.Button>
        </ButtonGroup>
      </Modal.Footer>
    </Modal>
  )
}
